import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import * as styles from './lifeQuote.css.ts';
import { useLanguage } from "../../../../hooks";
import { apiService } from "../../../shared/services/apiService";
import type { LifeQuoteItem } from '../../../../types';

export const LifeQuote: React.FC = memo((): JSX.Element | null => {
  const { language } = useLanguage();
  const [quoteData, setQuoteData] = useState<LifeQuoteItem | null>(null);
  const [displayText, setDisplayText] = useState('');
  const [cursorVisible, setCursorVisible] = useState(true);
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [isSkipped, setIsSkipped] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Use refs to store intervals
  const typingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const cursorIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadedRef = useRef(false); // ✅ AJOUT - Prevent multiple loads
  
  // ✅ CORRECTION : Clean up all intervals and timeouts
  const cleanupIntervals = useCallback(() => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    if (cursorIntervalRef.current) {
      clearInterval(cursorIntervalRef.current);
      cursorIntervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // ✅ CORRECTION : Load quote only once
  const loadActiveQuote = useCallback(async () => {
  // ✅ Prevent multiple simultaneous loads
  if (loadedRef.current) {
    return;
  }
  
  try {
    loadedRef.current = true;
    setLoading(true);

    // ✅ MODIFICATION - Essayer getActive puis fallback vers toutes les citations
    let response = await apiService.get('/life-quotes/active', true, {
      cacheTtlMs: 300000, // 5 minutes cache pour les citations
      useCache: true
    });
    // Si pas de citation active, essayer toutes les citations visibles
    if (!response?.data || (Array.isArray(response.data) && response.data.length === 0)) {
      response = await apiService.get('/life-quotes', true, {
        cacheTtlMs: 300000, // 5 minutes cache
        useCache: true
      });
      }
    
    // ✅ CORRECTION - Gestion correcte du type ApiResponse<LifeQuoteItem[]>
    if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
      // Prioriser les citations visibles et actives, sinon prendre les visibles
      const activeQuotes = response.data.filter((q: any) => q.is_visible && q.is_active);
      const visibleQuotes = response.data.filter((q: any) => q.is_visible);
      
      const quotesToUse = activeQuotes.length > 0 ? activeQuotes : visibleQuotes;
      
      if (quotesToUse.length > 0) {
        const randomIndex = Math.floor(Math.random() * quotesToUse.length);
        setQuoteData(quotesToUse[randomIndex]);
      } else {
        // ✅ Fallback avec citation statique si pas de données visibles
        setQuoteData({
          id: 'fallback-api',
          text_fr: "L'innovation distingue un leader d'un suiveur.",
          text_en: "Innovation distinguishes between a leader and a follower.",
          signature_fr: "Steve Jobs",
          signature_en: "Steve Jobs",
          is_active: true,
          is_visible: true,
          order_index: 1,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        } as LifeQuoteItem);
      }
    } else {
      // ✅ Fallback avec citation statique si pas de données de l'API
      setQuoteData({
        id: 'fallback-api-2',
        text_fr: "L'innovation distingue un leader d'un suiveur.",
        text_en: "Innovation distinguishes between a leader and a follower.",
        signature_fr: "Steve Jobs",
        signature_en: "Steve Jobs",
        is_active: true,
        is_visible: true,
        order_index: 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as LifeQuoteItem);
    }
  } catch (err) {
    // ✅ Fallback avec citation statique en cas d'erreur
    setQuoteData({
      id: 'fallback-1',
      text_fr: "Le code est de la poésie ; chaque ligne raconte une histoire, chaque fonction résout un mystère.",
      text_en: "Code is poetry; every line tells a story, every function solves a mystery.",
      signature_fr: "Développeur anonyme",
      signature_en: "Anonymous Developer",
      is_active: true,
      is_visible: true,
      order_index: 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    } as LifeQuoteItem);
  } finally {
    setLoading(false);
  }
}, []);

  // ✅ CORRECTION : Load data only once on mount
  useEffect(() => {
    loadActiveQuote();
    
    // ✅ Cleanup on unmount
    return () => {
      cleanupIntervals();
      loadedRef.current = false;
    };
  }, []); // ✅ CORRECTION MAJEURE - Dépendances vides pour éviter les boucles infinies

  // ✅ CORRECTION : Mémoiser les textes avec valeurs par défaut
  const quoteText = React.useMemo(() => {
    if (!quoteData) return '';
    const textKey = `text_${language}` as keyof LifeQuoteItem;
    return (quoteData[textKey] as string) || '';
  }, [quoteData, language]);

  const signature = React.useMemo(() => {
    if (!quoteData) return '';
    const signatureKey = `signature_${language}` as keyof LifeQuoteItem;
    return (quoteData[signatureKey] as string) || '';
  }, [quoteData, language]);
  
  // ✅ CORRECTION : Skip animation function
  const skipAnimation = useCallback(() => {
    if (!quoteText) return;
    
    cleanupIntervals();
    setIsSkipped(true);
    setDisplayText(quoteText);
    setIsTypingComplete(true);
  }, [quoteText, cleanupIntervals]);
  
  // ✅ CORRECTION : Effect pour l'animation (seulement quand nécessaire)
  useEffect(() => {
    // ✅ Conditions strictes pour éviter les boucles
    if (!quoteData || !quoteText || loading || isSkipped || isTypingComplete) {
      return;
    }
    
    // Reset state
    setDisplayText('');
    setIsTypingComplete(false);
    setCursorVisible(true);
    
    // Clean up previous intervals
    cleanupIntervals();
    
    // Start typing animation
    timeoutRef.current = setTimeout(() => {
      let index = 0;
      
      typingIntervalRef.current = setInterval(() => {
        if (index >= quoteText.length) {
          if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
          }
          setIsTypingComplete(true);
          return;
        }
        
        setDisplayText(quoteText.substring(0, index + 1));
        index++;
      }, 30); // Typing speed
    }, 300); // Initial delay
    
    return cleanupIntervals;
  }, [quoteData, quoteText, cleanupIntervals, isSkipped, loading, isTypingComplete]); // ✅ Inclure isTypingComplete pour éviter les re-déclenchements
  
  // ✅ CORRECTION : Effect pour le curseur (seulement pendant le typing)
  useEffect(() => {
    if (loading || isTypingComplete || isSkipped) {
      // Arrêter le curseur si l'animation est terminée
      if (cursorIntervalRef.current) {
        clearInterval(cursorIntervalRef.current);
        cursorIntervalRef.current = null;
      }
      return;
    }
    
    // Blinking cursor animation
    cursorIntervalRef.current = setInterval(() => {
      setCursorVisible(prev => !prev);
    }, 500);
    
    return () => {
      if (cursorIntervalRef.current) {
        clearInterval(cursorIntervalRef.current);
        cursorIntervalRef.current = null;
      }
    };
  }, [loading, isTypingComplete, isSkipped]); // ✅ Inclure isSkipped

  // ✅ CORRECTION : Effect pour gérer le changement de langue (reset une seule fois)
  useEffect(() => {
    if (quoteData && !loading && !isSkipped) {
      // Reset animation when language changes
      setDisplayText('');
      setIsTypingComplete(false);
      setCursorVisible(true);
      cleanupIntervals();
    }
  }, [language]); // ✅ Seulement language dans les dépendances

  // ✅ ÉTATS DE CHARGEMENT ET D'ERREUR
  if (loading) {
    return <div className={styles.quoteContainer}><div className={styles.loadingContainer}></div></div>;
  }

  // ✅ Toujours afficher quelque chose - fallback statique si aucune donnée
  if (!quoteData) {
    const fallbackText = language === 'fr'
      ? "Le code est de la poésie ; chaque ligne raconte une histoire."
      : "Code is poetry; every line tells a story.";
    const fallbackSignature = language === 'fr' ? 'Anonyme' : 'Anonymous';
    return (
      <div 
        className={styles.quoteContainer}
        aria-live="polite"
        aria-atomic="true"
        role="region"
        aria-label={language === 'fr' ? 'Citation inspirante' : 'Inspirational quote'}
      >
        <div className={styles.quoteContent}>
          <div className={`${styles.quoteSymbol} ${styles.complete}`} aria-hidden="true">"</div>
          <p className={styles.quote}>
            <span className={styles.quoteTextContent}>{fallbackText}</span>
          </p>
          <div className={`${styles.quoteName} ${styles.fadeIn}`} role="text">
            <span className={styles.signatureDash} aria-hidden="true">―</span>
            <span className={styles.signatureText}>{fallbackSignature}</span>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className={styles.quoteContainer}
      aria-live="polite"
      aria-atomic="true"
      role="region"
      aria-label={language === 'fr' ? 'Citation inspirante' : 'Inspirational quote'}
    >
      {/* Skip Button */}
      {!isTypingComplete && !isSkipped && quoteText && (
        <button 
          onClick={skipAnimation}
          className={styles.skipButton}
          aria-label={language === 'fr' ? 'Passer l\'animation' : 'Skip animation'}
          title={language === 'fr' ? 'Passer l\'animation' : 'Skip animation'}
        >
          <span className={styles.skipIcon}>⏭</span>
          <span className={styles.skipText}>
            {language === 'fr' ? 'Passer' : 'Skip'}
          </span>
        </button>
      )}
      
      <div className={styles.quoteContent}>
        {/* Quote Symbol */}
        <div 
          className={`${styles.quoteSymbol} ${isTypingComplete || isSkipped ? styles.complete : ''}`} 
          aria-hidden="true"
        >
          "
        </div>
        
        {/* Quote Text */}
        <p className={styles.quote}>
          <span className={styles.quoteTextContent}>
            {displayText}
          </span>
          
          {/* Cursor */}
          {!isTypingComplete && !isSkipped && (
            <span 
              className={`${styles.cursor} ${cursorVisible ? styles.visible : styles.hidden}`}
              aria-hidden="true"
            >
              |
            </span>
          )}
        </p>
        
        {/* Signature */}
        {signature && (isTypingComplete || isSkipped) && (
          <div 
            className={`${styles.quoteName} ${styles.fadeIn}`}
            role="text"
            aria-label={language === 'fr' ? `Auteur: ${signature}` : `Author: ${signature}`}
          >
            <span className={styles.signatureDash} aria-hidden="true">―</span>
            <span className={styles.signatureText}>{signature}</span>
          </div>
        )}
      </div>
      
      {/* Screen Reader Status */}
      <div className={styles.srOnly} aria-live="polite">
        {loading && (language === 'fr' ? 'Chargement...' : 'Loading...')}
        {isTypingComplete && (language === 'fr' ? 'Citation terminée' : 'Quote completed')}
      </div>
    </div>
  );
});

LifeQuote.displayName = 'LifeQuote';
export default LifeQuote;